// ResetPortfolioModal.js
// "The Comeback" — reset the user's main track record.
//   Desktop: centered modal.  Mobile: full-screen sheet.
// Sitewide type rule: Montserrat for the title, Poppins for subtitle/body.
import React, { useEffect } from "react";
import { LuRefreshCw, LuShieldCheck } from "react-icons/lu";
import ProcessingButton from "../Skeleton/ProcessingButton";


const RESETS = [
  "Account Value",
  "Holdings",
  "Trade History",
  "Start Date",
  "Performance Stats",
];
const STAYS = [
  "Username",
  "Followers",
  "Badges & Coins",
  "Competitions",
  "Posts & charts",
];


const ResetPortfolioModal = ({ isOpen, onClose, onConfirm, processing = false }) => {
  // Fully lock background scroll while the modal is open, so closing it returns
  // the user to where they were instead of scrolled far down the page.
  //   - overflow:hidden on <html> + <body> stops normal page scroll.
  //   - iOS Safari ignores that for touch, so we also block page `touchmove`,
  //     allowing scroll ONLY inside the modal's own scroll area ([data-modal-scroll]).
  // We deliberately avoid position:fixed on <body> — that mis-anchors the fixed
  // bottom nav on iOS and leaks across navigation.
  useEffect(() => {
    if (!isOpen) return;
    const { body } = document;
    const html = document.documentElement;
    const prevBody = body.style.overflow;
    const prevHtml = html.style.overflow;
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";

    const blockScroll = (e) => {
      // Allow finger-scrolling inside the modal; block everything else.
      if (e.target.closest && e.target.closest("[data-modal-scroll]")) return;
      e.preventDefault();
    };
    document.addEventListener("touchmove", blockScroll, { passive: false });

    return () => {
      body.style.overflow = prevBody;
      html.style.overflow = prevHtml;
      document.removeEventListener("touchmove", blockScroll);
    };
  }, [isOpen]);

  if (!isOpen) return null; // Don't render anything if the modal is not open

  return (
    <div className="fixed inset-0 z-[60] font-poppins">
      {/* Backdrop — click to dismiss */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Scroll host: full-screen sheet on mobile, centered modal on desktop.
          data-modal-scroll + overscroll-contain keep finger scrolling inside
          the modal and prevent it from chaining to the locked page. */}
      <div
        data-modal-scroll
        className="relative h-full w-full overflow-y-auto overscroll-contain flex md:items-center md:justify-center md:p-4"
      >
        <div className="relative bg-white w-full min-h-full md:min-h-0 md:max-w-2xl md:rounded-2xl md:shadow-xl p-5 md:p-8">
          {/* Header */}
          <div className="">
            <div className="min-w-0">
              {/* Title token: Montserrat, title color #004d4d (matches the
                  Leaderboard-treatment title). */}
              <h2 className="font-montserrat font-extrabold text-2xl md:text-[32px] leading-tight text-[#004d4d] pr-9">
                The Comeback
              </h2>
              <p className="mt-1 font-medium text-lg text-greenXD">
                {/* "main" is dropped on mobile (per the mobile design): mobile
                    -> "...for your track record"; desktop -> "...main track
                    record". md: = 768px, matching window.innerWidth < 768. */}
                A clean slate for your{" "}
                <span className="hidden md:inline">main </span>track record
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 mt-1 text-sm text-[#3F4848] hover:text-[#003434] absolute top-5 right-5"
            >
              Close
            </button>
          </div>

          {/* Intro */}
          <p className="mt-4 text-base leading-relaxed text-[#3F4848]">
            Reset your main track record for{" "}
            <span className="text-base font-semibold text-greenXD">$4.99</span> and start
            fresh with{" "}
            <span className="text-base font-semibold text-[#0F172A]">$1,000,000</span>.
            {/* Desktop: keep the whole second sentence together on line two
                ("This" shouldn't dangle at the end of line one). Mobile: inline. */}
            <span className="md:block">
              {" "}
              This gives you a new start date and a new track record.
            </span>
          </p>

          {/* What resets / What stays */}
          <div className="mt-5 grid grid-cols-2 gap-3 md:gap-6">
            {/* What resets */}
            <div className="rounded-xl border border-[#BFC8C8]/20 shadow-sm p-3.5 md:p-6">
              <div className="flex items-center gap-2 pb-2.5 mb-3 border-b-2 border-[#F08080]">
                {/* refresh.svg has a smaller 11x11 viewBox than circle-check's
                    14x14, so at the same box it scaled up bigger — render it a
                    touch smaller so it matches the "What stays" icon. */}
                <img src="/refresh.svg" className="w-3.5 h-3.5 shrink-0 text-[#F08080]" />
                <span className="font-normal text-sm md:text-lg text-[#003434]">
                  What resets
                </span>
              </div>
              <ul className="space-y-2.5 md:space-y-3">
                {RESETS.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 border-b border-[#BFC8C8]/20 pb-2">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-[#F08080]" />
                    <span className="text-xs md:text-base text-[#3F4848]">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* What stays */}
            <div className="rounded-xl border border-[#EEF1F1] shadow-sm p-3.5 md:p-6">
              <div className="flex items-center gap-2 pb-2.5 mb-3 border-b-2 border-greenXD">
                {/* Inline circle-check (LuCircleCheck isn't ESM-exported by the
                    installed react-icons, which breaks the build). */}
                <img src="/circle-check.svg" className="min-w-4 w-4 h-4 shrink-0 text-greenXD" />

                <span className="font-normal text-sm md:text-lg text-[#003434]">
                  What stays
                </span>
              </div>
              <ul className="space-y-2.5 md:space-y-3">
                {STAYS.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 border-b border-[#BFC8C8]/20 pb-2">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-greenXD" />
                    <span className="text-xs md:text-base text-[#3F4848]">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Record age matters */}
          <div className="mt-6 rounded-xl border border-[#006D35]/30 bg-[#FBFCFC] p-6">
            <div className="flex items-center gap-2 mb-2">
              <LuShieldCheck className="w-4 h-4 shrink-0 text-[#003434]" />
              <span className="font-bold text-base text-[#003434]">
                Record age matters
              </span>
            </div>
            <p className="text-sm leading-relaxed text-[#3F4848]">
              Only your main track record resets, and this{" "}
              <span className="font-semibold text-[#3F4848]">
                cannot be undone
              </span>
              .{" "}
              <span>
                Your return, P&amp;L, drawdown, and all other main performance stats
                reset too.{" "}
              </span>
              Longer track records carry more credibility on QUSHi.
            </p>
          </div>

          {/* Footer */}
          <p className="hidden md:block text-center text-xs text-[#003434] mt-6">
            Learn from it. Reset. Climb back.
          </p>
          {/* Inline processing while the payment intent is created — this used
              to dim the whole screen behind a centred spinner. Same fill, label
              swaps to `Starting`, input blocked so the $4.99 charge can't be
              double-submitted. */}
          <ProcessingButton
            onClick={onConfirm}
            processing={processing}
            label="Reset for $4.99"
            processingLabel="Starting"
            className="mt-4 w-full rounded-lg bg-primaryColor py-3 text-sm font-semibold text-white transition-opacity hover:opacity-95"
          />
          <button
            type="button"
            onClick={onClose}
            disabled={processing}
            aria-disabled={processing ? "true" : undefined}
            className="mt-3 w-full text-center text-sm font-semibold text-primaryColor"
          >
            Keep my current record
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPortfolioModal;
